import { useEffect, useMemo, useState } from 'react';
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';

const SHORT_UNITS = { year: 'y', month: 'mo', day: 'd', hour: 'h', minute: 'm' };

const getTimeAgoLabel = (value: number, unit: keyof typeof SHORT_UNITS, isShort: boolean): string => {
  const suffix = isShort ? SHORT_UNITS[unit] : `${unit}${value === 1 ? '' : 's'}`;
  return `${value}${isShort ? '' : ' '}${suffix} ago`;
};

const formatTimeAgo = (date: Date, isShort: boolean): string => {
  const now = new Date();

  const years = differenceInYears(now, date);
  if (years > 0) return getTimeAgoLabel(years, 'year', isShort);

  const months = differenceInMonths(now, date);
  if (months > 0) return getTimeAgoLabel(months, 'month', isShort);

  const days = differenceInDays(now, date);
  if (days > 0) return getTimeAgoLabel(days, 'day', isShort);

  const hours = differenceInHours(now, date);
  if (hours > 0) return getTimeAgoLabel(hours, 'hour', isShort);

  const minutes = differenceInMinutes(now, date);
  if (minutes > 0) return getTimeAgoLabel(minutes, 'minute', isShort);

  return 'just now';
};

type TAgoDateLabelProps = {
  children: string | number | Date; // Support Date objects and timestamps
  short?: boolean;
};

/**
 * AgoDateLabel Component
 * Automatically updates over time (e.g., 'just now' → '1 min ago').
 */
const AgoDateLabel = ({ children, short = false }: TAgoDateLabelProps) => {
  const date = useMemo(() => new Date(children), [children]);
  const [label, setLabel] = useState(() => formatTimeAgo(date, short));

  useEffect(() => {
    const update = () => setLabel(formatTimeAgo(date, short));
    update();

    const id = setInterval(update, 60_000); // update every minute
    return () => clearInterval(id);
  }, [date, short]);

  return <>{label}</>;
};

export { AgoDateLabel };
